class SectionFiltersCloudSearch {
  constructor(sidebarSelector = "#cloud_search_filters_sidebar") {
    this.sidebarSelector = sidebarSelector;
    this.applyButtonLabel = "Rechercher";

    this.TIMINGS = {
      initialSyncDelay: 220,
      initialExpandDelay: 350,
      mutationSyncDelay: 50,
      checkboxChangeSyncDelay: 20,
      applyClickSyncDelay: 25,
      filterNameClickSyncDelay: 40,
      defaultSyncDelay: 80,
      selectionFinalSyncDelay: 120,
      selectionUnlockDelay: 170,
      stepRecheckDelay: 35,
      stepRetryDelay: 20,
    };
    this.MAX_SELECTION_ATTEMPTS = 8;

    this.syncTimeout = null;
    this.observer = null;
    this.isSyncing = false;
    this.isApplyingSelectChange = false;
    this.selectionCycleId = 0;
    this.isReady = false;
    this.readyWatchInterval = null;
    this.readyWatchAttempts = 0;
    this.debugEnabled = false;

    this.boundOnChange = this.onSidebarChange.bind(this);
    this.boundOnClick = this.onSidebarClick.bind(this);

    this.init();
  }

  init() {
    this.sidebar = document.querySelector(this.sidebarSelector);
    if (!this.sidebar) return;

    this.xcloudFiltersMode =
      this.sidebar.getAttribute("data-xcloud-filters-mode") || "all";
    this.xcloudFiltersLimit =
      parseInt(this.sidebar.getAttribute("data-xcloud-filters-limit"), 10) || 4;
    this.xcloudFiltersWhitelist = (
      this.sidebar.getAttribute("data-xcloud-filters-whitelist") || ""
    )
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    this.filtersHeadingText = (
      this.sidebar.getAttribute("data-xcloud-filters-heading-text") || ""
    ).trim();
    this.filtersHeadingIcon =
      this.sidebar.getAttribute("data-xcloud-filters-heading-icon") || "";
    this.debugEnabled =
      this.sidebar.getAttribute("data-xcloud-debug") === "true";

    this.setLoadingState();
    this.startReadyWatch();

    this.observer = new MutationObserver((mutations) => {
      if (this.isSyncing) return;
      if (this.isApplyingSelectChange) return;

      const hasRelevantMutation = mutations.some((mutation) => {
        if (mutation.type !== "childList") return false;
        return (
          mutation.target === this.sidebar ||
          this.sidebar.contains(mutation.target)
        );
      });

      if (hasRelevantMutation) {
        this.scheduleSync(this.TIMINGS.mutationSyncDelay);
      }
    });

    this.observe();
    this.sidebar.addEventListener("change", this.boundOnChange);
    this.sidebar.addEventListener("click", this.boundOnClick);

    setTimeout(() => {
      this.expandFilters();
      this.scheduleSync(this.TIMINGS.initialSyncDelay);
    }, this.TIMINGS.initialExpandDelay);
  }

  hasPendingFiltersToExpand() {
    return Array.from(
      this.sidebar.querySelectorAll(".cloud-search-filter"),
    ).some(
      (filter) =>
        !filter.querySelector(".cloud-search-filter__values") &&
        filter.dataset.xcloudAutoExpanded !== "true",
    );
  }

  destroy() {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }

    this.stopReadyWatch();

    if (this.sidebar) {
      this.sidebar.removeEventListener("change", this.boundOnChange);
      this.sidebar.removeEventListener("click", this.boundOnClick);
    }

    this.disconnectObserver();
  }

  onSidebarChange(event) {
    if (this.isApplyingSelectChange) return;
    if (
      event.target.matches('.cloud-search-filter-value input[type="checkbox"]')
    ) {
      this.scheduleSync(this.TIMINGS.checkboxChangeSyncDelay);
    }
  }

  onSidebarClick(event) {
    if (this.isApplyingSelectChange) return;

    if (
      event.target.closest(".cloud-search-filters-apply") ||
      event.target.closest(".filters-sidebar-apply")
    ) {
      this.scheduleSync(this.TIMINGS.applyClickSyncDelay);
    }

    if (event.target.closest(".cloud-search-filter__name")) {
      this.scheduleSync(this.TIMINGS.filterNameClickSyncDelay);
    }
  }

  observe() {
    if (!this.sidebar || !this.observer) return;

    this.observer.observe(this.sidebar, {
      childList: true,
      subtree: true,
    });
  }

  disconnectObserver() {
    this.observer?.disconnect();
  }

  scheduleSync(delay = this.TIMINGS.defaultSyncDelay) {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }

    this.syncTimeout = setTimeout(() => {
      this.syncTimeout = null;
      this.sync();
    }, delay);
  }

  sync() {
    if (!this.sidebar) return;

    this.isSyncing = true;
    this.disconnectObserver();

    try {
      this.expandFilters();
      this.decorateSidebar();
      this.applyFiltersVisibility();
      this.applyHeadingText();
      this.applyApplyButton();
      this.renderThemeSelects();
      this.updateSelectOpacities();

      if (this.hasEnhancedMarkup() && !this.hasPendingFiltersToExpand()) {
        this.markReady();
      }
    } finally {
      this.observe();
      this.isSyncing = false;
    }
  }

  setLoadingState() {
    if (!this.sidebar) return;

    // Hide the sidebar immediately so Cloud Search's async injection doesn't flash raw markup.
    this.sidebar.style.opacity = "0";
    this.sidebar.style.pointerEvents = "none";

    const filtersContainer = this.sidebar.querySelector(
      ".cloud-search-filters-sidebar__filters",
    );
    if (filtersContainer) {
      filtersContainer.style.display = "none";
      let spinner = this.sidebar.querySelector(
        ".cloud-search-filters-loading-spinner",
      );
      if (!spinner) {
        spinner = document.createElement("div");
        spinner.className = "cloud-search-filters-loading-spinner";
        spinner.innerHTML = `
          <span class="visually-hidden">Chargement…</span>
          <svg class="spinner" width="32" height="32" viewBox="0 0 50 50">
            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
          </svg>
        `;
        filtersContainer.parentNode.insertBefore(spinner, filtersContainer);
      } else {
        spinner.style.display = "";
      }
    }
  }

  startReadyWatch() {
    this.stopReadyWatch();
    this.readyWatchAttempts = 0;
    this.readyWatchInterval = setInterval(() => {
      if (this.isReady) {
        this.stopReadyWatch();
        return;
      }

      if (this.hasEnhancedMarkup() && !this.hasPendingFiltersToExpand()) {
        this.markReady();
        return;
      }

      // Retry expansion here too: large filters (e.g. 2000+ options) can render
      // their toggle well after the single initial expandFilters() call.
      this.expandFilters();

      this.readyWatchAttempts += 1;
      this.scheduleSync(this.TIMINGS.mutationSyncDelay);

      if (this.readyWatchAttempts >= 60) {
        this.markReady();
      }
    }, 250);
  }

  stopReadyWatch() {
    if (!this.readyWatchInterval) return;
    clearInterval(this.readyWatchInterval);
    this.readyWatchInterval = null;
  }

  markReady() {
    if (!this.sidebar || this.isReady) return;

    this.isReady = true;

    const filtersContainer = this.sidebar.querySelector(
      ".cloud-search-filters-sidebar__filters",
    );
    if (filtersContainer) {
      filtersContainer.style.display = "";
    }
    const spinner = this.sidebar.querySelector(
      ".cloud-search-filters-loading-spinner",
    );
    if (spinner) spinner.remove();

    this.sidebar.style.opacity = "";
    this.sidebar.style.pointerEvents = "";

    this.stopReadyWatch();
  }

  hasEnhancedMarkup() {
    if (!this.sidebar) return false;
    return (
      this.sidebar.querySelector(".cloud-search-theme-select") !== null &&
      this.sidebar.querySelector(".filters-sidebar-apply") !== null
    );
  }

  decorateSidebar() {
    const sidebarPanels = this.sidebar.querySelectorAll(
      ".cloud-search-filters-sidebar",
    );

    sidebarPanels.forEach((panel) => {
      panel.classList.add("filters-sidebar", "section-filters-panel");

      const title = panel.querySelector(
        ".cloud-search-filters-sidebar__heading, .cloud-search-filters-sidebar__title",
      );
      if (title) {
        title.classList.add(
          "filters-sidebar__heading",
          "section-filters-panel-heading",
        );
      }

      const filtersContainer = panel.querySelector(
        ".cloud-search-filters-sidebar__filters",
      );
      if (filtersContainer) {
        filtersContainer.classList.add(
          "filters-sidebar__filters",
          "section-filters",
        );
      }
    });

    this.sidebar.querySelectorAll(".cloud-search-filter").forEach((filter) => {
      filter.classList.add("form-group", "filters-sidebar-filter");
    });
  }

  expandFilters() {
    const filters = Array.from(
      this.sidebar.querySelectorAll(".cloud-search-filter"),
    );

    filters.forEach((filter) => {
      if (filter.querySelector(".cloud-search-filter__values")) return;
      if (filter.dataset.xcloudAutoExpanded === "true") return;

      const toggle = filter.querySelector(".cloud-search-filter__name");
      if (!toggle) return;

      filter.dataset.xcloudAutoExpanded = "true";
      toggle.click();
    });
  }

  applyFiltersVisibility() {
    const filters = this.sidebar.querySelectorAll(".cloud-search-filter");

    filters.forEach((filter, index) => {
      let shouldShow = true;

      if (this.xcloudFiltersMode === "first_n") {
        shouldShow = index < this.xcloudFiltersLimit;
      } else if (
        this.xcloudFiltersMode === "whitelist" &&
        this.xcloudFiltersWhitelist.length > 0
      ) {
        const dataName = (filter.getAttribute("data-filter-name") || "")
          .trim()
          .toLowerCase();
        const labelName = (
          filter.querySelector(".cloud-search-filter__name span:last-child")
            ?.textContent || ""
        )
          .trim()
          .toLowerCase();
        shouldShow = this.xcloudFiltersWhitelist.includes(
          dataName || labelName,
        );
      }

      filter.style.display = shouldShow ? "" : "none";
    });
  }

  applyHeadingText() {
    if (!this.filtersHeadingText) return;

    const headingNodes = this.sidebar.querySelectorAll(
      ".cloud-search-filters-sidebar__heading, .cloud-search-filters-sidebar__title",
    );

    headingNodes.forEach((node) => {
      const currentText =
        node
          .querySelector(".cloud-search-filters-sidebar__heading-text")
          ?.textContent?.trim() || node.textContent.trim();

      if (
        currentText !== this.filtersHeadingText ||
        (this.filtersHeadingIcon && !node.querySelector(".filter-title-icon"))
      ) {
        node.innerHTML = `${this.filtersHeadingIcon}<span class="cloud-search-filters-sidebar__heading-text">${this.filtersHeadingText}</span>`;
      }
    });
  }

  applyApplyButton() {
    const dynamicButtons = this.sidebar.querySelectorAll(
      ".cloud-search-filters-apply",
    );
    dynamicButtons.forEach((button) => {
      button.textContent = this.applyButtonLabel;
      button.classList.add("filters-sidebar-apply-dynamic");
      button.style.display = "none";
    });

    const panels = this.sidebar.querySelectorAll(
      ".cloud-search-filters-sidebar",
    );
    panels.forEach((panel) => {
      let staticButton = panel.querySelector(".filters-sidebar-apply");

      if (!staticButton) {
        staticButton = document.createElement("button");
        staticButton.type = "button";
        staticButton.className =
          "filters-sidebar-apply button button-block button-secondary";
        staticButton.addEventListener("click", () => {
          const dynamicButton = panel.querySelector(
            ".cloud-search-filters-apply",
          );
          if (dynamicButton) {
            dynamicButton.click();
            return;
          }

          const submitButton = panel.querySelector(
            '.cloud-search-filters-sidebar__submit, button[type="submit"]',
          );
          submitButton?.click();
        });
        panel.appendChild(staticButton);
      }

      staticButton.textContent = this.applyButtonLabel;

      if (panel.lastElementChild !== staticButton) {
        panel.appendChild(staticButton);
      }
    });
  }

  renderThemeSelects() {
    const filters = this.sidebar.querySelectorAll(".cloud-search-filter");

    filters.forEach((filter, index) => {
      const filterNameNode = filter.querySelector(
        ".cloud-search-filter__name span:last-child",
      );
      const filterName = filterNameNode?.textContent?.trim();
      const valuesContainer = filter.querySelector(
        ".cloud-search-filter__values",
      );
      const filterValues = Array.from(
        filter.querySelectorAll(".cloud-search-filter-value"),
      );

      if (!filterName || !valuesContainer || filterValues.length === 0) return;

      let select = filter.querySelector(".cloud-search-theme-select");
      if (!select) {
        const wrapper = document.createElement("div");
        wrapper.className = "cloud-search-theme-select-wrapper";

        select = document.createElement("select");
        select.className =
          "dropdown-filter filters-sidebar-filter__name cloud-search-theme-select";
        wrapper.appendChild(select);
        valuesContainer.before(wrapper);

        select.addEventListener("change", (event) => {
          const liveFilter =
            event.target.closest(".cloud-search-filter") || filter;
          this.handleSelectChange(liveFilter, event.target.value);
          this.updateSelectOpacities();
        });
      }

      select.dataset.index = String(index + 1);
      this.syncSelectOptions(select, filterName, filterValues, filter);

      const filterNameHeader = filter.querySelector(
        ".cloud-search-filter__name",
      );
      if (filterNameHeader) {
        filterNameHeader.style.display = "none";
      }

      valuesContainer.style.display = "none";
    });
  }

  syncSelectOptions(select, filterName, filterValues, filter) {
    const checkedKeys = [];

    const nextOptions = [
      {
        value: "",
        text: filterName,
      },
    ];

    filterValues.forEach((filterValue, optionIndex) => {
      const checkbox = filterValue.querySelector('input[type="checkbox"]');
      const label = filterValue.querySelector(
        ".cloud-search-filter-value__name",
      );

      if (!checkbox || !label) return;

      const optionKey = this.getOptionKey(filterValue, optionIndex);
      filterValue.dataset.xcloudOptionKey = optionKey;

      if (checkbox.checked) {
        checkedKeys.push(optionKey);
      }

      nextOptions.push({
        value: optionKey,
        text: label.textContent.trim(),
      });
    });

    let checkedKey = "";
    if (checkedKeys.length > 0) {
      checkedKey = checkedKeys[0];
    }

    select.replaceChildren();

    nextOptions.forEach((optionData, optionIndex) => {
      const option = document.createElement("option");
      option.value = optionData.value;
      option.textContent = optionData.text;

      if (optionIndex === 0) {
        option.className = "default";
      }

      if (
        optionData.value === checkedKey ||
        (checkedKey === "" && optionData.value === "")
      ) {
        option.selected = true;
      }

      select.appendChild(option);
    });

    select.value = checkedKey;
    if (select.value !== checkedKey) {
      select.value = "";
    }
  }

  handleSelectChange(filter, selectedValue) {
    this.selectionCycleId += 1;
    const cycleId = this.selectionCycleId;

    this.isApplyingSelectChange = true;

    this.applySelectionStep(filter, selectedValue, 0, cycleId);
  }

  finalizeSelectChange(filter, selectedValue, cycleId) {
    if (cycleId !== this.selectionCycleId) return;

    this.enforceDeterministicSelection(filter, selectedValue);

    this.scheduleSync(this.TIMINGS.selectionFinalSyncDelay);
    setTimeout(() => {
      if (cycleId !== this.selectionCycleId) return;

      this.isApplyingSelectChange = false;
    }, this.TIMINGS.selectionUnlockDelay);
  }

  enforceDeterministicSelection(filter, selectedValue) {
    const optionsState = this.getFilterOptionsState(filter);
    if (optionsState.length === 0) return;

    if (selectedValue === "") {
      // On veut décocher toutes les cases (retour à l'état par défaut)
      optionsState.forEach((option) => {
        if (!option.checkbox.checked) return;
        option.checkbox.checked = false;
        option.checkbox.dispatchEvent(new Event("input", { bubbles: true }));
        option.checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      });
    } else {
      optionsState.forEach((option) => {
        const shouldBeChecked = option.optionKey === selectedValue;
        if (option.checkbox.checked === shouldBeChecked) return;
        option.checkbox.checked = shouldBeChecked;
        option.checkbox.dispatchEvent(new Event("input", { bubbles: true }));
        option.checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
  }

  getFilterOptionsState(filter) {
    const filterValues = Array.from(
      filter.querySelectorAll(".cloud-search-filter-value"),
    );
    return filterValues
      .map((filterValue, optionIndex) => {
        const checkbox = filterValue.querySelector('input[type="checkbox"]');
        if (!checkbox) return null;

        const optionKey =
          filterValue.dataset.xcloudOptionKey ||
          this.getOptionKey(filterValue, optionIndex);
        filterValue.dataset.xcloudOptionKey = optionKey;

        const label =
          filterValue
            .querySelector(".cloud-search-filter-value__name")
            ?.textContent?.trim() || "";

        return {
          filterValue,
          checkbox,
          optionKey,
          label,
          checked: Boolean(checkbox.checked),
        };
      })
      .filter(Boolean);
  }

  isSelectionStateReached(optionsState, selectedValue) {
    if (selectedValue === "") {
      return optionsState.every((option) => !option.checked);
    }

    return optionsState.every(
      (option) => option.checked === (option.optionKey === selectedValue),
    );
  }

  applySelectionStep(filter, selectedValue, attempt, cycleId) {
    if (cycleId !== this.selectionCycleId) return;

    if (!filter || !filter.isConnected) {
      this.isApplyingSelectChange = false;
      return;
    }

    const optionsState = this.getFilterOptionsState(filter);

    if (
      this.isSelectionStateReached(optionsState, selectedValue) ||
      attempt >= this.MAX_SELECTION_ATTEMPTS
    ) {
      this.finalizeSelectChange(filter, selectedValue, cycleId);
      return;
    }

    let optionToToggle = null;

    if (selectedValue === "") {
      optionToToggle = optionsState.find((option) => option.checked) || null;
    } else {
      // Certains composants refusent de décocher la valeur active tant que la nouvelle
      // valeur n'est pas activée; on coche donc la cible en premier.
      optionToToggle =
        optionsState.find(
          (option) => !option.checked && option.optionKey === selectedValue,
        ) ||
        optionsState.find(
          (option) => option.checked && option.optionKey !== selectedValue,
        ) ||
        null;
    }

    if (!optionToToggle) {
      this.finalizeSelectChange(filter, selectedValue, cycleId);
      return;
    }

    const toggleControl = optionToToggle.filterValue;
    toggleControl.click();

    setTimeout(() => {
      if (cycleId !== this.selectionCycleId) return;

      const liveState = this.getFilterOptionsState(filter);
      const liveOption = liveState.find(
        (option) => option.optionKey === optionToToggle.optionKey,
      );
      const expectedState =
        selectedValue === "" ? false : liveOption.optionKey === selectedValue;

      const hasToggled = liveOption?.checked === expectedState;

      if (!hasToggled && liveOption?.checkbox) {
        liveOption.checkbox.click();
        // Fallback robuste: forcer l'état si le click n'a pas été pris par le composant tiers.
        liveOption.checkbox.checked = !optionToToggle.checked;
        liveOption.checkbox.dispatchEvent(
          new Event("input", { bubbles: true }),
        );
        liveOption.checkbox.dispatchEvent(
          new Event("change", { bubbles: true }),
        );
      }

      setTimeout(() => {
        this.applySelectionStep(filter, selectedValue, attempt + 1, cycleId);
      }, this.TIMINGS.stepRetryDelay);
    }, this.TIMINGS.stepRecheckDelay);
  }

  getOptionKey(filterValue, optionIndex) {
    const checkbox = filterValue.querySelector('input[type="checkbox"]');
    const label =
      filterValue
        .querySelector(".cloud-search-filter-value__name")
        ?.textContent?.trim() || "";
    const id = checkbox?.id || "";
    const name = checkbox?.name || "";
    const value = checkbox?.value || "";

    // Utiliser une clé stable entre rerenders : l'index change souvent selon l'ordre injecté.
    if (id) return `id:${id}`;
    if (name || value) return `nv:${name}|${value}|${label}`;
    return `label:${label}|fallback:${optionIndex}`;
  }

  updateSelectOpacities() {
    const selects = Array.from(
      this.sidebar.querySelectorAll(".cloud-search-theme-select"),
    );
    if (selects.length === 0) return;

    const hasAnySelection = selects.some((select) => select.value !== "");

    selects.forEach((select, index) => {
      const isActive = hasAnySelection ? select.value !== "" : index === 0;
      const opacity = isActive ? "1" : "0.5";
      const group = select.closest(".filters-sidebar-filter");

      select.style.opacity = opacity;
      if (group) {
        group.style.opacity = opacity;
      }
    });
  }
}

let sectionFiltersCloudSearchInstance;

function initSectionFiltersCloudSearch() {
  if (sectionFiltersCloudSearchInstance) {
    sectionFiltersCloudSearchInstance.destroy();
  }

  sectionFiltersCloudSearchInstance = new SectionFiltersCloudSearch();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSectionFiltersCloudSearch);
} else {
  initSectionFiltersCloudSearch();
}

window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    initSectionFiltersCloudSearch();
  }
});
